<section class="hero">
  <div class="container hero__inner position-relative z-1">
    <div class="row">
      <div class="col-12 col-lg-7 d-flex flex-column align-items-start">
        <h1 class="hero__title display-2 mb-4">Everything you need to know about Microchipping</h1>
        <p class="hero__subtitle mb-4">
          If you've recently taken in a new pet, whether you've rescued from a shelter or purchased
          through a breeder, the chances are they've been microchipped already by a local vet – and
          if they haven't, you'll probably need to look into sorting this. But whether that's
          mandatory or not for your animal to be chipped depends entirely on what species they are –
          but first, let's cover the basics.
        </p>
        <app-trustbox class="align-self-center align-self-sm-start" theme="dark"></app-trustbox>
      </div>
    </div>
  </div>

  <div class="hero__image-col">
    <img
      src="/assets/images/identibase/microchipping-pet/microchipping-man-petting-cat.webp"
      alt="Person sitting on a sofa petting their cat"
      class="hero__image">
  </div>
</section>

<section class="basics">
  <div class="container">
    <div class="row gy-5">

      <div class="col-12 col-lg-6">
        <h2 class="basics__title display-4 mb-4">What is a Microchip?</h2>
        <p class="basics__body">
          A microchip is a small device which is implanted by a vet in between your pet's shoulders,
          under the skin, and measures in around 11-13mm – that's roughly the size of a grain of
          rice. The procedure is so quick and painless that your pet won't feel a thing and is so
          small that they'll never know it's there.
        </p>
        <p class="basics__body mb-0">
          A microchip's purpose is to identify your pet when scanned using the link between the
          implanted chip, and the database your pet's details remain on. Think of it a bit like a
          barcode or a QR code for your pet. Therefore, this can be a quick and useful way of being
          reunited if your pet goes missing. How?
        </p>
      </div>

      <div class="col-12 col-lg-6 d-flex flex-column gap-4">
        <div class="basics__step d-flex align-items-start gap-3">
          <span class="basics__badge d-flex align-items-center justify-content-center">
            <span class="basics__badge-square"></span>
            <span class="basics__badge-square basics__badge-square--rotated"></span>
            <span class="basics__badge-number">1</span>
          </span>
          <p class="basics__step-body mb-0">
            When your lost pet is found, it can be taken to any local vet or animal charity where it
            will be scanned with a small device for a microchip
          </p>
        </div>

        <div class="basics__step d-flex align-items-start gap-3">
          <span class="basics__badge d-flex align-items-center justify-content-center">
            <span class="basics__badge-square"></span>
            <span class="basics__badge-square basics__badge-square--rotated"></span>
            <span class="basics__badge-number">2</span>
          </span>
          <p class="basics__step-body mb-0">
            If your pet is microchipped, the scanner will find the microchip number and identify your
            pet along with a home address and contact number, providing you've kept your details up
            to date of course.
          </p>
        </div>

        <div class="basics__step d-flex align-items-start gap-3">
          <span class="basics__badge d-flex align-items-center justify-content-center">
            <span class="basics__badge-square"></span>
            <span class="basics__badge-square basics__badge-square--rotated"></span>
            <span class="basics__badge-number">3</span>
          </span>
          <p class="basics__step-body mb-0">
            Once the vet identifies your pet, they can then contact you and reunite you with your
            best friend in no time.
          </p>
        </div>
      </div>

    </div>
  </div>
</section>

<section class="next-steps">
  <div class="container">
    <div class="row justify-content-center text-center">
      <div class="col-12 col-lg-9">
        <h2 class="next-steps__title display-4 mb-4">My pet's been Microchipped – what next?</h2>
        <p class="next-steps__body mb-0">
          As soon as your pet has been chipped, you'll receive their chip number – this is a unique
          15-digit number aligned with your pet and will identify them should they ever wander too
          far. Once you have the chip number, you can register this with your chip's database –
          that's where we come in! With identi, it's completely free to register your pet and we
          offer three unique pet protection services to choose from to cover your pet should they
          ever be lost, missing or stolen.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="plans">
  <div class="container">
    <app-plan-grid></app-plan-grid>
  </div>
</section>

<section class="compulsory">
  <div class="container">

    <div class="row justify-content-center text-center">
      <div class="col-12 col-lg-9">
        <h2 class="compulsory__title display-4 mb-3">Is it compulsory to microchip my pet?</h2>
        <p class="compulsory__subtitle mb-5">
          This entirely depends on the species of your pet, so let's break it down.
        </p>
      </div>
    </div>

    <div class="d-flex flex-column gap-4">
      <div class="compulsory__card border">
        <h3 class="compulsory__card-title fs-4 mb-3">Dogs</h3>
        <p class="compulsory__card-body mb-0">
          In 2016 it became a legal requirement for all dogs and puppies in the UK to be
          microchipped at no later than eight weeks old – and you can be fined up to £500 if your dog
          is found not to be microchipped. You can arrange for a vet to administer the chip, or
          charities such as Dog's Trust, Blue Cross and Battersea will also be able to do this for
          free.
        </p>
      </div>

      <div class="compulsory__card border">
        <h3 class="compulsory__card-title fs-4 mb-3">Cats</h3>
        <p class="compulsory__card-body mb-0">
          As of June 2024 it will be compulsory for all cats and kittens over 20 weeks old to be
          microchipped. Failure to microchip your cat could result in a £500 fine. You can arrange
          for a qualified professional such as a vet to implant the chip, or charities such as Blue
          Cross and Cat's Protection will be able to do this for free.
        </p>
      </div>

      <div class="compulsory__card border">
        <h3 class="compulsory__card-title fs-4 mb-3">Horses</h3>
        <p class="compulsory__card-body mb-0">
          In 2020 it became mandatory for all horses, ponies and donkeys in the UK to be
          microchipped – and you can be fined up to £200 if your animal is found without one. A vet
          can administer the chip and this will cost between £25 - £30.
        </p>
      </div>

      <div class="compulsory__card border">
        <h3 class="compulsory__card-title fs-4 mb-3">Small Animals</h3>
        <p class="compulsory__card-body mb-0">
          Rabbits, Ferrets, Tortoises and other smaller animals can also be microchipped and although
          this isn't required by law, it's highly recommended and gives you the best possible chance
          of reuniting should they escape, go missing or be stolen.
        </p>
      </div>
    </div>

  </div>
</section>

<section class="tracking-info">
  <div class="container">
    <div class="row justify-content-center text-center">
      <div class="col-12 col-lg-8">
        <h2 class="tracking-info__title display-4 mb-4">
          Can I track my pet's location if they're microchipped?
        </h2>
        <p class="tracking-info__body mb-0">
          Microchip technology is more than often confused with that of a GPS device, but a
          microchip's sole purpose is to identify your pet when scanned, and contrary to popular
          belief, you cannot track your pet's live location via a microchip – but that's where our
          <span class="tracking-info__highlight">Locator</span> service comes in!
        </p>
      </div>
    </div>
  </div>
</section>
